import { useEffect } from 'react';
import { create } from 'zustand';

import { MochiButton } from '@autopass-web/mochi-component';
import { SuccessPaymentLayout } from '@autopass-web/mochi-payment';

import styles from './index.module.scss';

import { PageContainer } from '#/guest-aps/components/page-container';
import amplitude from '#/guest-aps/services/amplitude';
import { sessionStorageController } from '#/guest-aps/services/storages';
import { useBulletinBoards } from '#/guest-aps/services/use-bulletin-boards';

type DisplayValue = {
  plateNumber: string;
  priceAmountStr?: string;
  parkingRecordId: string;
  onClick: () => void;
};

type AlprPaymentViewStore = {
  value: DisplayValue | undefined;
  onDisplay: (value: DisplayValue) => void;
  onClose: () => void;
};

export const useAlprPaymentViewStore = create<AlprPaymentViewStore>((set) => ({
  value: undefined,
  onDisplay: (value: DisplayValue) => {
    set({
      value,
    });
  },
  onClose: () => {
    set({ value: undefined });
  },
}));

export const AlprPaymentView = () => {
  const { value } = useAlprPaymentViewStore();

  const parkinglotInfo = sessionStorageController.getParkinglot();
  const { apsBackgroundStyles } = useBulletinBoards(
    {
      codes:
        'aps_promotion, aps_partner_template, alpr_success_to_aps_with_info',
      partnerId: parkinglotInfo.partner_id,
    },
    { enabled: !!parkinglotInfo.partner_id },
  );

  useEffect(() => {
    if (!value) {
      return;
    }

    amplitude.track('引導會員車牌車辨離場頁_pageview', {
      停車場id: parkinglotInfo.partner_id,
      停車場名稱: parkinglotInfo.name,
      車牌號碼: value.plateNumber,
      parkingRecordId: value.parkingRecordId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!value) return null;

  const { plateNumber, onClick } = value;
  return (
    <div className={styles.alprView}>
      <PageContainer style={apsBackgroundStyles} invertFooter>
        <SuccessPaymentLayout
          bottom={
            <MochiButton
              type="button"
              variant="solid-round"
              size="full"
              theme="secondary"
              style={{ width: '80%', margin: '20px auto' }}
              onClick={() => {
                amplitude.track('引導會員車牌車辨離場頁-仍使用手動繳費_點擊', {
                  停車場id: parkinglotInfo.partner_id,
                  停車場名稱: parkinglotInfo.name,
                  車牌號碼: value.plateNumber,
                  parkingRecordId: value.parkingRecordId,
                });
                onClick();
              }}
            >
              仍使用手動繳費
            </MochiButton>
          }
        >
          <div className={styles.content}>
            <div className={styles.carPlate}>{plateNumber}</div>
            <div className={styles.block}>
              <div className={styles.desc}>
                已申辦 Autopass 停車快速通
                <br />
                <b>稍候請直接離場</b>
              </div>
            </div>
            {!!value?.priceAmountStr && (
              <>
                <div className={styles.block}>
                  <div className={styles.text}>預計折抵金額</div>
                  <div className={styles.discountAmount}>
                    {value.priceAmountStr} 元
                  </div>
                </div>
                <div className={styles.comment}>
                  （金額為預估，實際金額以現場為主）
                </div>
              </>
            )}
          </div>
        </SuccessPaymentLayout>
      </PageContainer>
    </div>
  );
};
